import JumboClient from '@grofers/jumbo-client-js/dist/jumbo'
import { useAuthStore } from '@/stores/useAuthStore'
import { PLATFORM_NAME_ENUM, PLATFORM_NAMES, USER_ENVIRONMENTS } from '@/constants/jumboEventNames'
import type {
  JumboEventName,
  UserEnvironment,
  JumboEventProperties,
  Traits,
  UserAgent,
} from '@/analytics/types'

const TABLE_NAME = 'social_ads_studio_events'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let jumboClient: any = null

if (typeof window !== 'undefined') {
  jumboClient = JumboClient.getInstance()
}
const SESSION_ID_KEY = 'jumbo_session_id'

const getOrCreateSessionId = (): string => {
  if (typeof sessionStorage === 'undefined') return ''

  const existing = sessionStorage.getItem(SESSION_ID_KEY)
  if (existing) return existing

  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`

  sessionStorage.setItem(SESSION_ID_KEY, id)
  return id
}

const sessionId = getOrCreateSessionId()

export const setJumboHeaders = () => {
  const { userId } = useAuthStore.getState() || null
  jumboClient.setParams({
    url: 'https://jumbo.blinkit.com/event',
    source: 'web',
    session_id: sessionId,
    user_id: userId,
  })
}

const getBrowser = (ua: string): string => {
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome'
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari'
  if (ua.includes('Firefox')) return 'Firefox'
  if (ua.includes('Edg')) return 'Edge'
  return 'Other'
}

const getUserAgent = (): UserAgent => {
  if (typeof navigator === 'undefined') {
    return { browser: 'Other' }
  }
  const ua = navigator.userAgent
  return {
    browser: getBrowser(ua),
  }
}

const getEnvironment = (): UserEnvironment => {
  if (typeof window === 'undefined') return USER_ENVIRONMENTS.USER_ENVIRONMENT_DEVELOPMENT
  const host = window.location.hostname
  if (host === 'localhost' || host === '127.0.0.1')
    return USER_ENVIRONMENTS.USER_ENVIRONMENT_DEVELOPMENT
  if (host.includes('dev')) return USER_ENVIRONMENTS.USER_ENVIRONMENT_STAGING
  return USER_ENVIRONMENTS.USER_ENVIRONMENT_PRODUCTION
}

const buildTraits = (): Traits => {
  const { userId } = useAuthStore.getState()
  return {
    user_environment: getEnvironment(),
    user_agent: getUserAgent(),
    user_id: userId ?? '',
    session_id: sessionId,
  }
}

const normalizePlatformName = (platformName?: string): string | undefined => {
  if (platformName == null) return undefined
  if (platformName === PLATFORM_NAME_ENUM.PLATFORM_NAME_META) return platformName
  if (platformName === PLATFORM_NAME_ENUM.PLATFORM_NAME_GOOGLE) return platformName
  if (platformName === PLATFORM_NAME_ENUM.PLATFORM_NAME_UNSPECIFIED) return platformName
  if (platformName === PLATFORM_NAMES.META) return PLATFORM_NAME_ENUM.PLATFORM_NAME_META
  if (platformName === PLATFORM_NAMES.GOOGLE) return PLATFORM_NAME_ENUM.PLATFORM_NAME_GOOGLE
  return PLATFORM_NAME_ENUM.PLATFORM_NAME_UNSPECIFIED
}

export const sendEventToJumbo = <T extends object>({
  eventName,
  eventProperties,
}: {
  eventName: JumboEventName
  eventProperties: T
}) => {
  if (getEnvironment() === USER_ENVIRONMENTS.USER_ENVIRONMENT_DEVELOPMENT) return

  const normalizedPlatformName = normalizePlatformName(
    (eventProperties as JumboEventProperties).platform_name
  )
  const eventValue = {
    event_name: eventName,
    properties: {
      ...eventProperties,
      ...(normalizedPlatformName !== undefined ? { platform_name: normalizedPlatformName } : {}),
    },
    traits: buildTraits(),
  }
  jumboClient.sendToJumbo(TABLE_NAME, eventValue)
  jumboClient.sendEventsToJumbo()
}
